/**
 * useCorrectionWait — entre la copie rendue et l'écran de fin, quand Sam corrige : l'attente, puis la
 * correction. Le moteur de l'ancienne `CorrectionSequence` (et de `CorrectionWait`), sans écran :
 *  - le relevé de la correction (`pollActivityCorrection`, la même lecture que `getActivities` sous un
 *    nom qu'aucun réducteur n'écoute) : 18 fois, une toutes les 5 s (`useCorrectionPolling`), puis le
 *    délai dépassé ; une relecture de l'activité (`getActivities`) quand l'attente prend fin ;
 *  - ses horloges : 2,6 s d'intro (« Sam corrige ta copie », seul), 1,3 s d'annonce (« Ta correction
 *    est prête ! ») avant la copie corrigée ;
 *  - l'avis de l'élève sur la correction (`api/feedbacks`), relu à l'ouverture de la copie ; un envoi
 *    qui échoue rend l'avis d'avant et se dit (l'ancien laissait l'avis affiché comme donné) ;
 *  - les mesures `correction.wait.started` et `correction.viewed` (le catalogue de monitoring les suit) ;
 *  - l'attente quittée avant la correction (`onLeftUnsettled`, au démontage) : le live la surveille et
 *    l'annonce quand elle arrive (`useCorrectionWatch`). Hors live, rien.
 * Le tirage du défi éclair ou de la pause est à l'écran (les questions arrivent avec `correction_wait`).
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"

import { activitiesService } from "../../../data/services/activitiesService"
import { apiCall, apiFailureOf, type ApiService } from "../../shared/apiCall"
import { createTelemetry } from "../../shared/telemetry"
import { deriveCorrectionFlow, submittedWorkImages } from "./correctionFlow"
import { useCorrectionPolling } from "./useCorrectionPolling"

/** « Sam corrige ta copie », seul à l'écran, le temps de le lire. */
export const CORRECTION_INTRO_MS = 2600
/** « Ta correction est prête ! », un instant avant qu'elle ne s'ouvre. */
export const CORRECTION_READY_BEAT_MS = 1300

export type CorrectionStep = "intro" | "waiting" | "ready" | "ending" | "copy"

/** L'activité rendue, telle que `finalize` puis le relevé la servent. */
export type CorrectedActivity = {
    id?: number | null
    status?: string | null
    correction_status?: string | null
    correction_image?: string | null
    correction_points?: unknown[] | null
    activity_logs?: { action?: string | null; work_attachment?: string | null }[] | null
} & Record<string, unknown>

export type CorrectionState = {
    reviewStatus: string | null
    status: string | null
    image: string | null
    points: unknown[] | null
    workImages: { uri: string }[] | null
    timedOut: boolean
}

/** L'étape à l'écran, de ce qu'on sait : pure (`CorrectionSequence` + `CorrectionWait`). */
export function correctionStep(input: {
    introDone: boolean
    reading: boolean
    completed: boolean
    endingText: string | null
}): CorrectionStep {
    if (input.reading) return "copy"
    if (input.completed) return "ready"
    if (input.endingText) return "ending"
    return input.introDone ? "waiting" : "intro"
}

/** Un relevé : ce qu'il apprend, ou rien (une correction prête le reste). */
export function mergeCorrection(
    current: CorrectionState,
    fresh: CorrectedActivity | null | undefined
): CorrectionState {
    if (fresh == null) return current
    const reviewStatus = fresh.status ?? current.reviewStatus
    const status = fresh.correction_status ?? current.status
    if (current.status === "completed" || (reviewStatus === current.reviewStatus && status === current.status)) {
        return current
    }
    const freshWorkImages = submittedWorkImages(fresh)
    return {
        ...current,
        reviewStatus,
        status,
        image: fresh.correction_image ?? current.image,
        points: fresh.correction_points ?? current.points,
        workImages: freshWorkImages.length ? freshWorkImages : current.workImages,
    }
}

export type UseCorrectionWait = {
    step: CorrectionStep
    endingText: string | null
    correction: CorrectionState
    feedback: "positive" | "negative" | null
    feedbackSubmitting: boolean
    /** L'envoi de l'avis a échoué : il est revenu (le message du serveur, s'il en a un). */
    feedbackFailure: { message: string | null } | null
    submitFeedback: (value: "positive" | "negative") => void
}

type Dispatch = (action: never) => unknown

const telemetry = createTelemetry("activity.correction", { role: "student" })

export type UseCorrectionWaitOptions = {
    /** L'attente se démonte alors que la correction est encore en route (délai dépassé compris). */
    onLeftUnsettled?: () => void
}

export function useCorrectionWait(
    activity: CorrectedActivity,
    options: UseCorrectionWaitOptions = {}
): UseCorrectionWait {
    const dispatch = useDispatch() as unknown as Dispatch
    const activityId = activity.id ?? null

    const [correction, setCorrection] = useState<CorrectionState>(() => ({
        reviewStatus: activity.status ?? null,
        status: activity.correction_status ?? null,
        image: activity.correction_image ?? null,
        points: activity.correction_points ?? null,
        workImages: null,
        timedOut: false,
    }))

    const readCopy = useCallback(
        (service: ApiService) =>
            apiCall<CorrectedActivity[]>(dispatch, service, {
                urlParams: { with_logs: 1, activity_ids: activityId },
            }),
        [activityId, dispatch]
    )

    const { awaiting, completed, endingText } = deriveCorrectionFlow({
        reviewStatus: correction.reviewStatus,
        correctionStatus: correction.status,
        timedOut: correction.timedOut,
    })

    // Quittée avant la correction, l'attente le dit en partant ; prête, échouée ou sans correction, rien.
    const stillComing = deriveCorrectionFlow({
        reviewStatus: correction.reviewStatus,
        correctionStatus: correction.status,
    }).awaiting
    const leaving = useRef({ stillComing, onLeftUnsettled: options.onLeftUnsettled })
    leaving.current = { stillComing, onLeftUnsettled: options.onLeftUnsettled }
    useEffect(
        () => () => {
            if (leaving.current.stillComing) leaving.current.onLeftUnsettled?.()
        },
        []
    )

    const [introDone, setIntroDone] = useState(false)
    useEffect(() => {
        const timer = setTimeout(() => setIntroDone(true), CORRECTION_INTRO_MS)
        return () => clearTimeout(timer)
    }, [])

    const [reading, setReading] = useState(false)
    useEffect(() => {
        if (!completed) return
        const timer = setTimeout(() => setReading(true), CORRECTION_READY_BEAT_MS)
        return () => clearTimeout(timer)
    }, [completed])

    useEffect(() => {
        amplitude.track("correction.wait.started", { activity_id: activityId })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    useEffect(() => {
        if (reading) amplitude.track("correction.viewed", { activity_id: activityId })
    }, [reading, activityId])

    // La fin de l'attente relit l'activité une fois, pour que le store porte sa correction.
    const wasAwaiting = useRef(false)
    useEffect(() => {
        if (wasAwaiting.current && !awaiting) {
            readCopy(activitiesService.getActivities as ApiService).catch((error) =>
                telemetry.error("refresh_after_wait", error)
            )
        }
        wasAwaiting.current = awaiting
    }, [awaiting, readCopy])

    useCorrectionPolling(
        awaiting,
        () => {
            readCopy(activitiesService.pollActivityCorrection as ApiService)
                .then((rows) => {
                    const fresh = Array.isArray(rows) ? rows.find((row) => row?.id === activityId) : null
                    setCorrection((current) => mergeCorrection(current, fresh))
                })
                .catch((error) => telemetry.error("poll", error))
        },
        () => setCorrection((current) => ({ ...current, timedOut: true }))
    )

    // L'avis sur la correction, relu à l'ouverture de la copie corrigée.
    const [feedback, setFeedback] = useState<"positive" | "negative" | null>(null)
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false)
    const [feedbackFailure, setFeedbackFailure] = useState<{ message: string | null } | null>(null)
    useEffect(() => {
        if (!reading || activityId == null) return
        apiCall<{ feedback_type?: "positive" | "negative" } | null>(
            dispatch,
            activitiesService.getActivityCorrectionFeedback as ApiService,
            { routeParams: { activityId } }
        )
            .then((data) => {
                if (data?.feedback_type) setFeedback(data.feedback_type)
            })
            .catch((error) => telemetry.error("feedback_read", error))
    }, [reading, activityId, dispatch])

    const submitFeedback = useCallback(
        (value: "positive" | "negative") => {
            if (activityId == null || feedbackSubmitting) return
            // L'avis d'avant revient si celui-ci ne part pas.
            const before = feedback
            setFeedbackSubmitting(true)
            setFeedbackFailure(null)
            setFeedback(value)
            apiCall(dispatch, activitiesService.submitActivityCorrectionFeedback as ApiService, {
                data: { activityId, feedbackType: value },
            }).then(
                () => setFeedbackSubmitting(false),
                (error: unknown) => {
                    telemetry.error("feedback_submit", error)
                    setFeedback(before)
                    setFeedbackFailure({ message: apiFailureOf(error)?.message ?? null })
                    setFeedbackSubmitting(false)
                }
            )
        },
        [activityId, dispatch, feedback, feedbackSubmitting]
    )

    return {
        step: correctionStep({ introDone, reading, completed, endingText }),
        endingText,
        correction,
        feedback,
        feedbackSubmitting,
        feedbackFailure,
        submitFeedback,
    }
}
